/**
 * HierarchyView
 * Displays users in a collapsible hierarchical tree structure
 * Mobile-optimized with reduced indentation and overflow menu
 */

import React, { useState, useRef, useEffect } from 'react';
import Button from '../common/Button';
import { useMediaQuery } from '../../hooks/useMediaQuery';

// Chevron icons
const ChevronDown = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronRight = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

// Role badges
const getRoleBadge = (role) => {
  const badges = {
    director: 'bg-red-100 text-red-800',
    manager: 'bg-purple-100 text-purple-800',
    leader: 'bg-blue-100 text-blue-800',
    agent: 'bg-green-100 text-green-800',
    telemarketer: 'bg-orange-100 text-orange-800',
    sid: 'bg-yellow-100 text-yellow-800',
    asid: 'bg-amber-100 text-amber-800',
    designer: 'bg-pink-100 text-pink-800',
    draughter_2d: 'bg-indigo-100 text-indigo-800',
    draughter_site: 'bg-violet-100 text-violet-800',
    quotation_team: 'bg-teal-100 text-teal-800',
    legal_team: 'bg-slate-100 text-slate-800',
    operation_head: 'bg-cyan-100 text-cyan-800',
    site_manager: 'bg-emerald-100 text-emerald-800',
    site_supervisor: 'bg-lime-100 text-lime-800'
  };
  return badges[role] || 'bg-gray-100 text-gray-800';
};

const getRoleDisplayName = (role) => {
  const names = {
    director: 'Director',
    manager: 'Manager',
    leader: 'Leader',
    agent: 'Agent',
    telemarketer: 'Telemarketer',
    sid: 'SID',
    asid: 'ASID',
    designer: 'Designer',
    draughter_2d: '2D Draughter',
    draughter_site: 'Site Draughter',
    quotation_team: 'Quotation',
    legal_team: 'Legal',
    operation_head: 'Operation Head',
    site_manager: 'Site Manager',
    site_supervisor: 'Site Supervisor'
  };
  return names[role] || role;
};

// More menu icon
const MoreIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
  </svg>
);

// Dropdown menu for mobile actions
function ActionMenu({ user, onEdit, onResign, onReactivate, onDelete, currentUserId, isSystemAdmin }) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const canResign = !user.isResigned && user.id !== currentUserId;
  const canReactivate = user.isResigned && user.id !== currentUserId;
  const canDelete = isSystemAdmin && user.id !== currentUserId;

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
        aria-label="More actions"
      >
        <MoreIcon />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 z-30 overflow-hidden">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(user);
              setIsOpen(false);
            }}
            className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </button>
          {canResign && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onResign(user);
                setIsOpen(false);
              }}
              className="w-full px-4 py-2.5 text-left text-sm text-amber-600 hover:bg-amber-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Resign
            </button>
          )}
          {canReactivate && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onReactivate(user);
                setIsOpen(false);
              }}
              className="w-full px-4 py-2.5 text-left text-sm text-green-600 hover:bg-green-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reactivate
            </button>
          )}
          {canDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(user);
                setIsOpen(false);
              }}
              className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// User row component - Mobile optimized
function UserRow({ user, indent = 0, onEdit, onResign, onReactivate, onDelete, currentUserId, isSystemAdmin, showStatus = true }) {
  const isMobile = useMediaQuery('(max-width: 767px)');

  // Mobile: use smaller indentation with border indicator
  // Desktop: use larger indentation
  const getIndentClass = () => {
    if (isMobile) {
      return indent === 1 ? 'pl-3 border-l-2 border-blue-300 ml-2' :
             indent === 2 ? 'pl-3 border-l-2 border-green-300 ml-4' : '';
    }
    return indent === 1 ? 'pl-8' : indent === 2 ? 'pl-16' : '';
  };

  return (
    <div className={`flex items-center justify-between py-3 px-2 sm:px-4 hover:bg-gray-50 active:bg-gray-100 border-b border-gray-100 ${getIndentClass()}`}>
      <div className="flex items-center flex-1 min-w-0">
        {/* Avatar */}
        <div className="flex-shrink-0 h-9 w-9 rounded-full bg-gradient-gold flex items-center justify-center mr-2 sm:mr-3">
          <span className="text-white font-medium text-xs">
            {user.nickname ? user.nickname.substring(0, 2).toUpperCase() : '??'}
          </span>
        </div>

        {/* Name, Email, and Role */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <span className="font-medium text-gray-900 truncate text-sm sm:text-base">{user.nickname}</span>
            <span className={`badge text-xs ${getRoleBadge(user.role)}`}>
              {getRoleDisplayName(user.role)}
            </span>
            {user.isResigned && (
              <span className="badge text-xs bg-gray-200 text-gray-600">Resigned</span>
            )}
          </div>
          <div className="text-xs sm:text-sm text-gray-500 truncate">{user.fullName}</div>
          <div className="text-xs text-gray-400 truncate hidden sm:block">{user.email}</div>
        </div>
      </div>

      {/* Actions - Mobile: overflow menu, Desktop: buttons */}
      <div className="flex items-center gap-2 ml-2 sm:ml-4">
        {isMobile ? (
          <ActionMenu
            user={user}
            onEdit={onEdit}
            onResign={onResign}
            onReactivate={onReactivate}
            onDelete={onDelete}
            currentUserId={currentUserId}
            isSystemAdmin={isSystemAdmin}
          />
        ) : (
          <>
            <Button variant="outline-gold" size="sm" onClick={() => onEdit(user)}>
              Edit
            </Button>
            {!user.isResigned && user.id !== currentUserId && (
              <Button variant="outline-danger" size="sm" onClick={() => onResign(user)}>
                Resign
              </Button>
            )}
            {user.isResigned && user.id !== currentUserId && (
              <Button variant="outline-success" size="sm" onClick={() => onReactivate(user)}>
                Reactivate
              </Button>
            )}
            {isSystemAdmin && user.id !== currentUserId && (
              <Button variant="danger" size="sm" onClick={() => onDelete(user)}>
                Delete
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Collapsible section for a supervisor (Manager, Leader, or SID) - Mobile optimized
function CollapsibleSection({
  supervisor,
  members,
  defaultExpanded = false,
  onEdit,
  onResign,
  onReactivate,
  onDelete,
  currentUserId,
  isSystemAdmin,
  colorClass = 'border-blue-500',
  bgClass = 'bg-blue-50',
  children
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const activeMembers = members.filter(m => !m.isResigned).length;
  const totalMembers = members.length;

  return (
    <div className={`border-l-4 ${colorClass} rounded-lg overflow-hidden mb-3 bg-white shadow-sm`}>
      {/* Supervisor Header */}
      <div
        className={`flex items-center justify-between px-2 sm:px-4 py-3 cursor-pointer ${bgClass} hover:opacity-90 active:opacity-80 transition-opacity`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center flex-1 min-w-0">
          <button className="mr-1.5 sm:mr-2 text-gray-600 flex-shrink-0">
            {expanded ? <ChevronDown /> : <ChevronRight />}
          </button>

          <div className="flex-shrink-0 h-9 sm:h-10 w-9 sm:w-10 rounded-full bg-gradient-gold flex items-center justify-center mr-2 sm:mr-3">
            <span className="text-white font-semibold text-xs sm:text-sm">
              {supervisor.nickname ? supervisor.nickname.substring(0, 2).toUpperCase() : '??'}
            </span>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 text-sm sm:text-base truncate">{supervisor.nickname}</span>
              <span className={`badge text-xs ${getRoleBadge(supervisor.role)}`}>
                {getRoleDisplayName(supervisor.role)}
              </span>
              {supervisor.isResigned && (
                <span className="badge text-xs bg-gray-200 text-gray-600">Resigned</span>
              )}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 truncate">{supervisor.fullName}</div>
            <div className="text-xs text-gray-500 truncate hidden sm:block">{supervisor.email}</div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-3 ml-2" onClick={e => e.stopPropagation()}>
          {/* Member count badge */}
          <span className="text-xs sm:text-sm text-gray-600 bg-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded whitespace-nowrap">
            {isMobile ? activeMembers : `${activeMembers} active / ${totalMembers} total`}
          </span>

          {/* Actions */}
          {isMobile ? (
            <ActionMenu
              user={supervisor}
              onEdit={onEdit}
              onResign={onResign}
              onReactivate={onReactivate}
              onDelete={onDelete}
              currentUserId={currentUserId}
              isSystemAdmin={isSystemAdmin}
            />
          ) : (
            <>
              <Button variant="outline-gold" size="sm" onClick={() => onEdit(supervisor)}>
                Edit
              </Button>
              {!supervisor.isResigned && supervisor.id !== currentUserId && (
                <Button variant="outline-danger" size="sm" onClick={() => onResign(supervisor)}>
                  Resign
                </Button>
              )}
              {supervisor.isResigned && supervisor.id !== currentUserId && (
                <Button variant="outline-success" size="sm" onClick={() => onReactivate(supervisor)}>
                  Reactivate
                </Button>
              )}
              {isSystemAdmin && supervisor.id !== currentUserId && (
                <Button variant="danger" size="sm" onClick={() => onDelete(supervisor)}>
                  Delete
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-gray-200">
          {children ? children : (
            members.length > 0 ? (
              members.map(member => (
                <UserRow
                  key={member.id}
                  user={member}
                  indent={1}
                  onEdit={onEdit}
                  onResign={onResign}
                  onReactivate={onReactivate}
                  onDelete={onDelete}
                  currentUserId={currentUserId}
                  isSystemAdmin={isSystemAdmin}
                />
              ))
            ) : (
              <div className="py-4 px-4 sm:px-8 text-gray-500 text-sm italic">No team members</div>
            )
          )}
        </div>
      )}
    </div>
  );
}

// Sales Team Hierarchy: Manager → Leader → Agents
export function SalesHierarchy({ users, onEdit, onResign, onReactivate, onDelete, currentUserId, isSystemAdmin }) {
  // Get all managers
  const managersData = users.filter(u => u.role === 'manager');
  const leaders = users.filter(u => u.role === 'leader');
  const agents = users.filter(u => u.role === 'agent');
  const telemarketers = users.filter(u => u.role === 'telemarketer');

  // Leaders without a manager
  const unassignedLeaders = leaders.filter(l => !l.managerId);

  // Agents/Telemarketers without a leader
  const unassignedSalesStaff = [...agents, ...telemarketers].filter(a => !a.leaderId);

  if (managersData.length === 0 && leaders.length === 0 && agents.length === 0 && telemarketers.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No sales team members found
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Managers with their teams */}
      {managersData.map(manager => {
        const managerLeaders = leaders.filter(l => l.managerId === manager.id);

        return (
          <CollapsibleSection
            key={manager.id}
            supervisor={manager}
            members={managerLeaders}
            defaultExpanded={true}
            onEdit={onEdit}
            onResign={onResign}
            onReactivate={onReactivate}
            onDelete={onDelete}
            currentUserId={currentUserId}
            isSystemAdmin={isSystemAdmin}
            colorClass="border-purple-500"
            bgClass="bg-purple-50"
          >
            {managerLeaders.length > 0 ? (
              managerLeaders.map(leader => {
                const leaderAgents = [...agents, ...telemarketers]
                  .filter(a => a.leaderId === leader.id)
                  .sort((a, b) => {
                    if (a.role !== b.role) return a.role === 'agent' ? -1 : 1;
                    return a.nickname.localeCompare(b.nickname);
                  });

                return (
                  <div key={leader.id} className="ml-4 border-l-2 border-blue-300">
                    <CollapsibleSection
                      supervisor={leader}
                      members={leaderAgents}
                      defaultExpanded={false}
                      onEdit={onEdit}
                      onResign={onResign}
                      onReactivate={onReactivate}
                      onDelete={onDelete}
                      currentUserId={currentUserId}
                      isSystemAdmin={isSystemAdmin}
                      colorClass="border-blue-400"
                      bgClass="bg-blue-50"
                    />
                  </div>
                );
              })
            ) : (
              <div className="py-4 px-8 text-gray-500 text-sm italic">No leaders assigned</div>
            )}
          </CollapsibleSection>
        );
      })}

      {/* Unassigned Leaders */}
      {unassignedLeaders.length > 0 && (
        <div className="border-l-4 border-gray-400 rounded-lg overflow-hidden bg-gray-50 p-4">
          <h4 className="font-semibold text-gray-700 mb-3 flex items-center">
            <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Leaders Without Manager ({unassignedLeaders.length})
          </h4>
          {unassignedLeaders.map(leader => {
            const leaderAgents = [...agents, ...telemarketers].filter(a => a.leaderId === leader.id);
            return (
              <CollapsibleSection
                key={leader.id}
                supervisor={leader}
                members={leaderAgents}
                defaultExpanded={false}
                onEdit={onEdit}
                onResign={onResign}
                onReactivate={onReactivate}
                onDelete={onDelete}
                currentUserId={currentUserId}
                isSystemAdmin={isSystemAdmin}
                colorClass="border-blue-400"
                bgClass="bg-blue-50"
              />
            );
          })}
        </div>
      )}

      {/* Unassigned Agents/Telemarketers */}
      {unassignedSalesStaff.length > 0 && (
        <div className="border-l-4 border-gray-400 rounded-lg overflow-hidden bg-gray-50 p-4">
          <h4 className="font-semibold text-gray-700 mb-3 flex items-center">
            <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Unassigned Staff ({unassignedSalesStaff.length})
          </h4>
          <div className="bg-white rounded-lg overflow-hidden">
            {unassignedSalesStaff.map(user => (
              <UserRow
                key={user.id}
                user={user}
                onEdit={onEdit}
                onResign={onResign}
                onReactivate={onReactivate}
                onDelete={onDelete}
                currentUserId={currentUserId}
                isSystemAdmin={isSystemAdmin}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Design Team Hierarchy: SID → ASIDs, Designers, 2D Draughters
export function DesignHierarchy({ users, onEdit, onResign, onReactivate, onDelete, currentUserId, isSystemAdmin }) {
  const sidsData = users.filter(u => u.role === 'sid');
  const asids = users.filter(u => u.role === 'asid');
  const designers = users.filter(u => u.role === 'designer');
  const draughters2d = users.filter(u => u.role === 'draughter_2d');
  const siteDraughters = users.filter(u => u.role === 'draughter_site');

  // Team members without SID
  const unassignedDesignStaff = [...asids, ...designers, ...draughters2d].filter(u => !u.sidId);

  if (sidsData.length === 0 && asids.length === 0 && designers.length === 0 && draughters2d.length === 0 && siteDraughters.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No design team members found
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* SIDs with their teams */}
      {sidsData.map(sid => {
        const sidTeam = [
          ...asids.filter(u => u.sidId === sid.id),
          ...designers.filter(u => u.sidId === sid.id),
          ...draughters2d.filter(u => u.sidId === sid.id)
        ].sort((a, b) => {
          const roleOrder = { asid: 1, designer: 2, draughter_2d: 3 };
          if (roleOrder[a.role] !== roleOrder[b.role]) {
            return roleOrder[a.role] - roleOrder[b.role];
          }
          return a.nickname.localeCompare(b.nickname);
        });

        // Group by role for better display
        const teamAsids = sidTeam.filter(u => u.role === 'asid');
        const teamDesigners = sidTeam.filter(u => u.role === 'designer');
        const teamDraughters = sidTeam.filter(u => u.role === 'draughter_2d');

        return (
          <CollapsibleSection
            key={sid.id}
            supervisor={sid}
            members={sidTeam}
            defaultExpanded={true}
            onEdit={onEdit}
            onResign={onResign}
            onReactivate={onReactivate}
            onDelete={onDelete}
            currentUserId={currentUserId}
            isSystemAdmin={isSystemAdmin}
            colorClass="border-yellow-500"
            bgClass="bg-yellow-50"
          >
            <div className="divide-y divide-gray-100">
              {/* ASIDs */}
              {teamAsids.length > 0 && (
                <div className="py-2">
                  <div className="px-4 py-2 text-xs font-semibold text-amber-700 bg-amber-50 uppercase tracking-wider">
                    ASIDs ({teamAsids.length})
                  </div>
                  {teamAsids.map(user => (
                    <UserRow
                      key={user.id}
                      user={user}
                      indent={1}
                      onEdit={onEdit}
                      onResign={onResign}
                      onReactivate={onReactivate}
                      onDelete={onDelete}
                      currentUserId={currentUserId}
                      isSystemAdmin={isSystemAdmin}
                    />
                  ))}
                </div>
              )}

              {/* Designers */}
              {teamDesigners.length > 0 && (
                <div className="py-2">
                  <div className="px-4 py-2 text-xs font-semibold text-pink-700 bg-pink-50 uppercase tracking-wider">
                    Designers ({teamDesigners.length})
                  </div>
                  {teamDesigners.map(user => (
                    <UserRow
                      key={user.id}
                      user={user}
                      indent={1}
                      onEdit={onEdit}
                      onResign={onResign}
                      onReactivate={onReactivate}
                      onDelete={onDelete}
                      currentUserId={currentUserId}
                      isSystemAdmin={isSystemAdmin}
                    />
                  ))}
                </div>
              )}

              {/* 2D Draughters */}
              {teamDraughters.length > 0 && (
                <div className="py-2">
                  <div className="px-4 py-2 text-xs font-semibold text-indigo-700 bg-indigo-50 uppercase tracking-wider">
                    2D Draughters ({teamDraughters.length})
                  </div>
                  {teamDraughters.map(user => (
                    <UserRow
                      key={user.id}
                      user={user}
                      indent={1}
                      onEdit={onEdit}
                      onResign={onResign}
                      onReactivate={onReactivate}
                      onDelete={onDelete}
                      currentUserId={currentUserId}
                      isSystemAdmin={isSystemAdmin}
                    />
                  ))}
                </div>
              )}

              {sidTeam.length === 0 && (
                <div className="py-4 px-8 text-gray-500 text-sm italic">No team members</div>
              )}
            </div>
          </CollapsibleSection>
        );
      })}

      {/* Site Draughters (shared resources) */}
      {siteDraughters.length > 0 && (
        <div className="border-l-4 border-violet-500 rounded-lg overflow-hidden bg-violet-50 p-4">
          <h4 className="font-semibold text-violet-800 mb-3 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Site Draughters - Shared Resources ({siteDraughters.length})
          </h4>
          <div className="bg-white rounded-lg overflow-hidden">
            {siteDraughters.map(user => (
              <UserRow
                key={user.id}
                user={user}
                onEdit={onEdit}
                onResign={onResign}
                onReactivate={onReactivate}
                onDelete={onDelete}
                currentUserId={currentUserId}
                isSystemAdmin={isSystemAdmin}
              />
            ))}
          </div>
        </div>
      )}

      {/* Unassigned Design Staff */}
      {unassignedDesignStaff.length > 0 && (
        <div className="border-l-4 border-gray-400 rounded-lg overflow-hidden bg-gray-50 p-4">
          <h4 className="font-semibold text-gray-700 mb-3 flex items-center">
            <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Unassigned Staff ({unassignedDesignStaff.length})
          </h4>
          <div className="bg-white rounded-lg overflow-hidden">
            {unassignedDesignStaff.map(user => (
              <UserRow
                key={user.id}
                user={user}
                onEdit={onEdit}
                onResign={onResign}
                onReactivate={onReactivate}
                onDelete={onDelete}
                currentUserId={currentUserId}
                isSystemAdmin={isSystemAdmin}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Site Team Hierarchy: Site Manager → Site Supervisors
export function SiteTeamHierarchy({ users, onEdit, onResign, onReactivate, onDelete, currentUserId, isSystemAdmin }) {
  const operationHeads = users.filter(u => u.role === 'operation_head');
  const siteManagers = users.filter(u => u.role === 'site_manager');
  const siteSupervisors = users.filter(u => u.role === 'site_supervisor');

  // Supervisors without site manager
  const unassignedSupervisors = siteSupervisors.filter(u => !u.siteManagerId);

  if (operationHeads.length === 0 && siteManagers.length === 0 && siteSupervisors.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No site team members found
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Operation Heads */}
      {operationHeads.length > 0 && (
        <div className="border-l-4 border-cyan-500 rounded-lg overflow-hidden bg-cyan-50 p-4">
          <h4 className="font-semibold text-cyan-800 mb-3 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Operation Heads ({operationHeads.length})
          </h4>
          <div className="bg-white rounded-lg overflow-hidden">
            {operationHeads.map(user => (
              <UserRow
                key={user.id}
                user={user}
                onEdit={onEdit}
                onResign={onResign}
                onReactivate={onReactivate}
                onDelete={onDelete}
                currentUserId={currentUserId}
                isSystemAdmin={isSystemAdmin}
              />
            ))}
          </div>
        </div>
      )}

      {/* Site Managers with their supervisors */}
      {siteManagers.map(siteManager => {
        const managerSupervisors = siteSupervisors
          .filter(u => u.siteManagerId === siteManager.id)
          .sort((a, b) => a.nickname.localeCompare(b.nickname));

        return (
          <CollapsibleSection
            key={siteManager.id}
            supervisor={siteManager}
            members={managerSupervisors}
            defaultExpanded={true}
            onEdit={onEdit}
            onResign={onResign}
            onReactivate={onReactivate}
            onDelete={onDelete}
            currentUserId={currentUserId}
            isSystemAdmin={isSystemAdmin}
            colorClass="border-emerald-500"
            bgClass="bg-emerald-50"
          >
            <div className="divide-y divide-gray-100">
              {managerSupervisors.length > 0 ? (
                <div className="py-2">
                  <div className="px-4 py-2 text-xs font-semibold text-lime-700 bg-lime-50 uppercase tracking-wider">
                    Site Supervisors ({managerSupervisors.length})
                  </div>
                  {managerSupervisors.map(user => (
                    <UserRow
                      key={user.id}
                      user={user}
                      indent={1}
                      onEdit={onEdit}
                      onResign={onResign}
                      onReactivate={onReactivate}
                      onDelete={onDelete}
                      currentUserId={currentUserId}
                      isSystemAdmin={isSystemAdmin}
                    />
                  ))}
                </div>
              ) : (
                <div className="py-4 px-8 text-gray-500 text-sm italic">No site supervisors assigned</div>
              )}
            </div>
          </CollapsibleSection>
        );
      })}

      {/* Unassigned Site Supervisors */}
      {unassignedSupervisors.length > 0 && (
        <div className="border-l-4 border-gray-400 rounded-lg overflow-hidden bg-gray-50 p-4">
          <h4 className="font-semibold text-gray-700 mb-3 flex items-center">
            <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Unassigned Site Supervisors ({unassignedSupervisors.length})
          </h4>
          <div className="bg-white rounded-lg overflow-hidden">
            {unassignedSupervisors.map(user => (
              <UserRow
                key={user.id}
                user={user}
                onEdit={onEdit}
                onResign={onResign}
                onReactivate={onReactivate}
                onDelete={onDelete}
                currentUserId={currentUserId}
                isSystemAdmin={isSystemAdmin}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default { SalesHierarchy, DesignHierarchy, SiteTeamHierarchy };
